import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  AlertTriangle, Stethoscope, Search, Scissors, Pill, Flame, Heart,
  Clock, Calendar, MessageCircle, Phone, ArrowLeft, ArrowRight, ChevronRight
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const conditions = [
  {
    title: "Endometriosis",

    about:
      "Endometriosis is a condition where tissue similar to the uterine lining grows outside the uterus, causing pain and sometimes fertility issues.",

    symptoms: [
      "Severe menstrual cramps",
      "Chronic pelvic pain",
      "Pain during intercourse",
      "Heavy or irregular periods",
      "Difficulty conceiving",
      "Pain during bowel movements or urination",
    ],

    whenToConsult: [
      "If menstrual pain affects daily life",
      "If you are facing infertility",
      "If pain worsens over time despite medication",
    ],

    treatments: [
      "Pain management medications",
      "Hormonal therapy",
      "Laparoscopic excision surgery (gold standard treatment)",
      "Fertility-focused treatment in selected cases",
    ],
  },

  {
    title: "Ovarian Cysts",

    about:
      "Ovarian cysts are fluid-filled sacs in or on the ovary. Most are benign, but some may require monitoring or surgery.",

    symptoms: [
      "Pelvic pain or heaviness",
      "Bloating or abdominal swelling",
      "Irregular menstrual cycles",
      "Pain during intercourse",
      "Sudden severe pain (if rupture occurs)",
    ],

    whenToConsult: [
      "Persistent pelvic pain",
      "Large or growing cyst detected on ultrasound",
      "Severe or sudden abdominal pain",
      "Irregular cycles or hormonal symptoms",
    ],

    treatments: [
      "Observation and regular monitoring",
      "Hormonal therapy (in selected cases)",
      "Laparoscopic/ robotic cyst removal (cystectomy)",
      "Emergency surgery (if rupture or torsion occurs)",
    ],
  },

  {
    title: "Uterine Fibroids",

    about:
      "Fibroids are non-cancerous growths in the uterus that are very common in women of reproductive age.",

    symptoms: [
      "Heavy or prolonged menstrual bleeding",
      "Pelvic pressure or pain",
      "Frequent urination",
      "Bloating or abdominal enlargement",
      "Difficulty conceiving",
      "Back pain",
    ],

    whenToConsult: [
      "Heavy bleeding affecting daily life",
      "Suspected fibroids on ultrasound",
      "Fertility issues",
      "Increasing abdominal size or discomfort",
    ],

    treatments: [
      "Medical management (hormonal therapy)",
      "Minimally invasive Robotic/ laparoscopic myomectomy",
      "Hysteroscopic fibroid removal",
      "Hysterectomy (in severe or recurrent cases)",
    ],
  },
];

const treatments = [
  {
    icon: Scissors,
    title: "Laparoscopic Surgery",
    description: "",
    points: [
      "Laparoscopic surgery",
    ],
  },

  {
    icon: Pill,
    title: "Hysteroscopic Procedures",
    description: "",
    points: [
      "Hysteroscopic procedures",
    ],
  },

  {
    icon: Flame,
    title: "Robotic-Assisted Surgery",
    description: "",
    points: [
      "Robotic-assisted surgery (if needed)",
    ],
  },

  {
    icon: Heart,
    title: "Medical Management",
    description: "",
    points: [
      "Medical (non-surgical) management",
    ],
  },

  {
    icon: Stethoscope,
    title: "Pain & Hormonal Management",
    description: "",
    points: [
      "Pain and hormonal management plans",
    ],
  },
];

const recoveryTimeline = [
  {
    phase: "After Laparoscopic/ Robotic Surgery",
    items: [
      "Hospital stay: 1–3 days",
      "Initial recovery: 1–2 weeks",
      "Full recovery: 3–4 weeks",
    ],
  },

  {
    phase: "After Medical Treatment",
    items: [
      "Symptom improvement with ongoing treatment and follow-up",
      "Regular follow-up required",
    ],
  },
];

const faqs = [
  {
    q: "Are ovarian cysts always dangerous?",
    a: "No, most ovarian cysts are harmless and resolve on their own, but some require treatment.",
  },

  {
    q: "Can endometriosis be cured?",
    a: "It can be effectively managed with surgery and medication, but long-term control is important.",
  },

  {
    q: "Do fibroids always need surgery?",
    a: "No, small fibroids may not need treatment unless they cause symptoms.",
  },

  {
    q: "Can I get pregnant with these conditions?",
    a: "Yes, many women conceive successfully with proper treatment and management.",
  },

  {
    q: "Is robotic/ laparoscopic surgery safe?",
    a: "Yes, it is a safe and preferred minimally invasive option in most cases.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55 } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

export default function OvarianCancerPage() {
  return (
    <>
      <Helmet>
        <title>
          Endometriosis, Ovarian Cysts & Fibroid Treatment in Kolkata | Dr Neha Agarwal
        </title>

        <meta
          name="description"
          content="Consult Dr Neha Agarwal for advanced treatment of endometriosis, ovarian cysts and uterine fibroids in Kolkata with minimally invasive laparoscopic and robotic surgery."
        />

        <meta
          name="keywords"
          content="endometriosis treatment Kolkata, ovarian cyst doctor Kolkata, fibroid treatment Kolkata, laparoscopic gynecologist Kolkata"
        />
      </Helmet>
      <div className="bg-[#F9F9F6] min-h-screen">
        {/* ─── STICKY HEADER ─── */}
        {/* <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F9F9F6]/80 border-b border-[#1E3F33]/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 group" data-testid="service-header-logo">
              <span className="font-serif text-2xl md:text-3xl font-semibold text-[#1E3F33] tracking-tight">
                Dr. Neha Agarwal
              </span>
            </Link>
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="service-back-home"
                className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-[#4A6359] hover:text-[#1E3F33] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
              <a
                href="tel:+916291000829"
                data-testid="service-header-call"
                className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
              >
                <Phone className="w-4 h-4" />
                Call Now
              </a>
            </div>
          </div>
        </header> */}

        {/* ─── HERO / INTRO ─── */}
        <section className="pt-28 md:pt-36 pb-16 md:pb-24" data-testid="service-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            {/* Breadcrumb */}
            <motion.nav
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2 text-sm text-[#4A6359] mb-8"
              data-testid="service-breadcrumb"
            >
              <Link to="/" className="hover:text-[#1E3F33] transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/#services" className="hover:text-[#1E3F33] transition-colors">Services</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#1E3F33] font-medium">Non Cancer </span>
            </motion.nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="lg:col-span-7"
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Gynecologic Oncology
                </p>
                <h1
                  data-testid="service-page-title"
                  style={{ lineHeight: "1.2" }}
                  className="font-serif text-4xl sm:text-3xl lg:text-6xl xl:text-5xl tracking-tighter leading-[0.95] text-[#1E3F33] mb-6"
                >
                  Endometriosis, Ovarian Cysts & Uterine Tumours Treatment in Kolkata – Expert Gynecologic Care by Dr. Neha Agarwal
                </h1>
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                  Not all gynecologic conditions are cancerous, but many still require expert evaluation and treatment to prevent complications and preserve fertility and quality of life. </p>
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                  Conditions like endometriosis, ovarian cysts, and uterine tumours (fibroids) are common among women and can significantly affect health, fertility, and daily comfort.</p>
                <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed max-w-2xl mb-8" data-testid="service-intro">
                  Dr. Neha Agarwal, a gynecologic oncologist and advanced laparoscopic surgeon in Kolkata, provides specialized diagnosis and minimally invasive treatment for both benign (non-cancerous) and complex gynecologic conditions.Her approach focuses on accurate diagnosis, fertility preservation where possible, and minimally invasive treatment option</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href="/book-appointment"
                    data-testid="service-hero-book-btn"
                    className="inline-flex items-center gap-2 bg-[#1E3F33] text-[#F9F9F6] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#2C5545] transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    Book Appointment
                  </a>
                  <a
                    href="https://wa.me/916291000829"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="service-hero-whatsapp-btn"
                    className="inline-flex items-center gap-2 border-2 border-[#1E3F33] text-[#1E3F33] px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-[#1E3F33] hover:text-[#F9F9F6] transition-all"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </a>
                </div>
              </motion.div>

              {/* Quick Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="lg:col-span-5"
              >
                <div className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8" data-testid="service-quick-info">
                  <h3 className="font-serif text-2xl text-[#1E3F33] mb-5">
                    Quick Overview
                  </h3>

                  <div className="space-y-4">

                    {/* Specialist */}
                    <div className="flex items-start gap-3">
                      <Stethoscope className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                      <div>
                        <p className="text-sm font-semibold text-[#1E3F33]">
                          Specialist
                        </p>

                        <p className="text-sm text-[#4A6359]">
                          Dr. Neha Agarwal, Gynecologic Oncologist & Advanced Laparoscopic Surgeon
                        </p>
                      </div>
                    </div>

                    {/* Conditions Treated */}
                    <div className="flex items-start gap-3">
                      <Heart className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                      <div>
                        <p className="text-sm font-semibold text-[#1E3F33]">
                          Conditions Treated
                        </p>

                        <p className="text-sm text-[#4A6359]">
                          Endometriosis, Ovarian Cysts & Uterine Fibroids
                        </p>
                      </div>
                    </div>

                    {/* Treatment Approaches */}
                    <div className="flex items-start gap-3">
                      <Scissors className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                      <div>
                        <p className="text-sm font-semibold text-[#1E3F33]">
                          Treatment Approaches
                        </p>

                        <p className="text-sm text-[#4A6359]">
                          Laparoscopic Surgery, Robotic Surgery, Hormonal Therapy & Fertility-Preserving Care
                        </p>
                      </div>
                    </div>

                    {/* Key Message */}
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-[#8FA998] mt-0.5 flex-shrink-0" />

                      <div>
                        <p className="text-sm font-semibold text-[#1E3F33]">
                          Key Message
                        </p>

                        <p className="text-sm text-[#4A6359]">
                          Early diagnosis and minimally invasive treatment help improve quality of life and fertility outcomes
                        </p>
                      </div>
                    </div>

                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
        {conditions.map((condition, index) => (
          <div key={index}>

            {/* ─── ABOUT SECTION ─── */}
            <section className="py-16 md:py-24 bg-[#F2EFE9]">
              <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                >
                  <h2 className="font-serif text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1E3F33] mb-6">
                    What is {condition.title}?
                  </h2>

                  <p className="text-base sm:text-lg text-[#4A6359] leading-relaxed">
                    {condition.about}
                  </p>
                </motion.div>

              </div>
            </section>

            {/* ─── SYMPTOMS ─── */}
            <section className="py-20 md:py-28 bg-[#1E3F33]">
              <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">

                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-80px" }}
                  variants={stagger}
                >
                  <motion.div variants={fadeUp} className="mb-10">

                    <div className="flex items-center gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-[#C29F60]" />

                      <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#C29F60]">
                        Warning Signs
                      </p>
                    </div>

                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight">
                      Symptoms of {condition.title}
                    </h2>
                  </motion.div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

                    {condition.symptoms.map((item, i) => (
                      <motion.div
                        key={i}
                        variants={fadeUp}
                        className="bg-[#2C5545] rounded-xl p-5 border border-[#8FA998]/15"
                      >
                        <div className="flex items-start gap-3">

                          <div className="w-6 h-6 rounded-full bg-[#C29F60]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-xs font-bold text-[#C29F60]">
                              {i + 1}
                            </span>
                          </div>

                          <p className="text-[#F9F9F6] text-sm sm:text-base font-medium">
                            {item}
                          </p>

                        </div>
                      </motion.div>
                    ))}

                  </div>
                </motion.div>

              </div>
            </section>

            {/* ─── WHEN TO CONSULT ─── */}
            <section className="py-20 md:py-28 bg-[#F9F9F6]">
              <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">

                  <motion.div variants={fadeUp}>
                    <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                      Expert Guidance
                    </p>

                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight">
                      When to Consult
                    </h2>
                  </motion.div>

                  <motion.div variants={stagger} className="space-y-0">

                    {condition.whenToConsult.map((item, i) => (
                      <motion.div
                        key={i}
                        variants={fadeUp}
                        className="flex items-start gap-4 py-5 border-b border-[#D8DDD9] last:border-0"
                      >
                        <div className="w-8 h-8 rounded-lg bg-[#1E3F33]/10 flex items-center justify-center flex-shrink-0">
                          <ChevronRight className="w-4 h-4 text-[#1E3F33]" />
                        </div>

                        <p className="text-base sm:text-lg text-[#1E3F33] font-medium leading-relaxed">
                          {item}
                        </p>
                      </motion.div>
                    ))}

                  </motion.div>

                </div>

              </div>
            </section>

            {/* ─── TREATMENT OPTIONS ─── */}
            <section className="py-20 md:py-28 bg-[#F2EFE9]">
              <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">

                <motion.div variants={fadeUp} className="mb-12">
                  <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33]">
                    Treatment Options
                  </h2>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                  {condition.treatments.map((item, i) => (
                    <div
                      key={i}
                      className="bg-[#F9F9F6] rounded-2xl p-6"
                    >
                      <div className="flex items-start gap-3">

                        <div className="w-8 h-8 rounded-full bg-[#1E3F33] flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[#F9F9F6]">
                            {i + 1}
                          </span>
                        </div>

                        <p className="text-[#1E3F33] text-sm sm:text-base font-medium leading-relaxed">
                          {item}
                        </p>

                      </div>
                    </div>
                  ))}

                </div>

              </div>
            </section>

          </div>
        ))}

        {/* ─── TREATMENT OPTIONS ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-treatment-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-14"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Treatment Approach
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-3xl" data-testid="service-treatment-heading">
                Dr. Neha Agarwal focuses on minimally invasive and fertility-preserving techniques whenever possible:
              </h2>
              {/* <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-2xl leading-relaxed">
              Dr. Neha Agarwal offers comprehensive screening for gynecologic cancers in Kolkata:          </p> */}
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
            >
              {treatments.map((t, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-treatment-card-${i}`}
                  className="bg-[#F2EFE9] rounded-2xl md:rounded-[2rem] p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-[#1E3F33]/10 flex items-center justify-center">
                      <t.icon className="w-6 h-6 text-[#1E3F33]" />
                    </div>
                    <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33] leading-snug">{t.title}</h3>
                  </div>
                  <p className="text-sm sm:text-base text-[#4A6359] mb-4 leading-relaxed">{t.description}</p>
                  <ul className="space-y-2.5">
                    {t.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#8FA998] mt-2 flex-shrink-0" />
                        <span className="text-sm text-[#1E3F33] leading-relaxed">{p}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── RECOVERY TIMELINE ─── */}
        <section className="py-20 md:py-28 bg-[#F2EFE9]" data-testid="service-recovery-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
              className="mb-12"
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                Recovery Journey
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl" data-testid="service-recovery-heading">
                Recovery Timeline
              </h2>
              <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-xl leading-relaxed">
                Recovery is generally faster than traditional surgery.
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={stagger}
              className="grid grid-cols-1 md:grid-cols-3 gap-6"
            >
              {recoveryTimeline.map((phase, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  data-testid={`service-recovery-phase-${i}`}
                  className="bg-[#F9F9F6] rounded-2xl p-7 border border-[#D8DDD9]"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-8 h-8 rounded-full bg-[#1E3F33] flex items-center justify-center">
                      <span className="text-xs font-bold text-[#F9F9F6]">{i + 1}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#1E3F33]">{phase.phase}</h3>
                  </div>
                  <ul className="space-y-3">
                    {phase.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <Clock className="w-4 h-4 text-[#8FA998] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-[#4A6359] leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="py-20 md:py-28 bg-[#F9F9F6]" data-testid="service-faq-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
                  Common Questions
                </p>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1E3F33] leading-tight" data-testid="service-faq-heading">
                  Frequently Asked Questions
                </h2>
                <p className="text-base sm:text-lg text-[#4A6359] mt-4 leading-relaxed">
                  Find answers to common questions about ovarian cancer diagnosis, treatment options, and recovery.
                </p>
              </motion.div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={fadeUp}
              >
                <Accordion type="single" collapsible className="w-full" data-testid="service-faq-accordion">
                  {faqs.map((faq, i) => (
                    <AccordionItem
                      key={i}
                      value={`faq-${i}`}
                      className="border-b border-[#D8DDD9] py-1"
                      data-testid={`service-faq-item-${i}`}
                    >
                      <AccordionTrigger
                        data-testid={`service-faq-trigger-${i}`}
                        className="font-serif text-lg sm:text-xl text-[#1E3F33] hover:no-underline py-5 text-left"
                      >
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#4A6359] text-base leading-relaxed pb-5">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ─── FINAL CTA ─── */}
        <section className="py-20 md:py-28 bg-[#1E3F33]" data-testid="service-final-cta">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              variants={fadeUp}
            >
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-6">
                Get Expert Treatment for Women’s Health Conditions in Kolkata
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#F9F9F6] leading-tight max-w-3xl mx-auto mb-6" data-testid="service-final-cta-heading">
                If you are experiencing pelvic pain, heavy bleeding, or have been diagnosed with endometriosis, ovarian cysts, or fibroids, early evaluation can help prevent complication
              </h2>
              <p className="text-base sm:text-lg text-[#8FA998] max-w-xl mx-auto leading-relaxed mb-10">
                Consult Dr. Neha Agarwal for advanced and minimally invasive gynecologic care in Kolkata.
              </p>

              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="/book-appointment"
                  data-testid="service-cta-book-btn"
                  className="inline-flex items-center gap-2 bg-[#F9F9F6] text-[#1E3F33] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#E6E7E2] transition-colors"
                >
                  <Calendar className="w-4 h-4" />
                  Book Appointment
                </a>
                <a
                  href="tel:+916291000829"
                  data-testid="service-cta-call-btn"
                  className="inline-flex items-center gap-2 border-2 border-[#F9F9F6] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#F9F9F6] hover:text-[#1E3F33] transition-all"
                >
                  <Phone className="w-4 h-4" />
                  Get Second Opinion
                </a>
                <a
                  href="https://wa.me/916291000829"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="service-cta-whatsapp-btn"
                  className="inline-flex items-center gap-2 bg-[#2C5545] text-[#F9F9F6] px-8 py-4 rounded-full text-sm font-bold hover:bg-[#8FA998]/30 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ─── SEO FOOTER ─── */}
        <footer className="bg-[#1E3F33] border-t border-[#F9F9F6]/10 py-12" data-testid="service-footer">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <h3 className="font-serif text-lg text-[#F9F9F6]/50 mb-3">
              Endometriosis, Ovarian Cysts & Fibroid Treatment in Kolkata
            </h3>
            <p className="text-xs text-[#F9F9F6]/30 leading-relaxed max-w-3xl mb-6">
              Looking for expert treatment for endometriosis, ovarian cysts, or uterine fibroids in Kolkata? Dr. Neha Agarwal provides advanced laparoscopic and medical management for women across Mukundapur, EM Bypass, Garia, Kasba, Ballygunge, Salt Lake, and nearby areas.
            </p>
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-[#F9F9F6]/10">
              <p className="text-xs text-[#F9F9F6]/30">
                &copy; {new Date().getFullYear()} Dr. Neha Agarwal. All rights reserved.
              </p>
              <Link to="/" className="text-xs text-[#F9F9F6]/30 hover:text-[#F9F9F6]/60 transition-colors flex items-center gap-1">
                <ArrowLeft className="w-3 h-3" /> Back to Home
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
